import React, { useState, useEffect } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Badge } from 'primereact/badge';
import { Card } from 'primereact/card';
import { InputTextarea } from 'primereact/inputtextarea';
import { makeRequest2 } from '../../../../../utils/makeRequest';
import { toastify } from '../../../../../utils/toast';
import { useFacilityStore } from '../../../../../app/z-store/store';
import { getItem } from '../../../../../utils/localStorage';

const localizer = momentLocalizer(moment);

const ExpenseCalendarTab = () => {
  const selectedFacilityId = useFacilityStore((state) => state.facilityId);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);
  const [showDayDialog, setShowDayDialog] = useState(false);
  const [dayExpenses, setDayExpenses] = useState([]);
  const [view, setView] = useState('month');
  const [date, setDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [comments, setComments] = useState('');
  const [commentsDialogVisible, setCommentsDialogVisible] = useState(false);
  const [approvalAction, setApprovalAction] = useState(null);
  const [selectedExpenseId, setSelectedExpenseId] = useState(null);

  // Data state
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [budgets, setBudgets] = useState([]);

  useEffect(() => {
    getItem("APPUSER").then(userData => {
      if (typeof userData === 'string') userData = JSON.parse(userData);
      setCurrentUser(userData?.user || null);
    });
  }, []);

  useEffect(() => {
    if (selectedFacilityId) {
      fetchExpenses();
      fetchCategories();
      fetchBudgets();
    }
  }, [selectedFacilityId]);

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const res = await makeRequest2(`/api/app/expense_management/expenses/get_all/${selectedFacilityId}`, 'GET');
      setExpenses(res?.data?.expenses || []);
    } catch (err) {
      console.error(err);
      toastify('Error fetching expenses', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await makeRequest2(`/api/app/expense_management/expense_category/get_all_expense_categories/${selectedFacilityId}`, 'GET');
      setCategories(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchBudgets = async () => {
    try {
      const res = await makeRequest2(`/api/app/expense_management/get_budgets/${selectedFacilityId}`, 'GET');
      setBudgets(res?.data?.budgets || []);
    } catch (err) {
      console.error(err);
    }
  };

  const isUserApprover = (expense) => {
    if (!currentUser?.userId || !Array.isArray(expense.approvals)) return false;
    const step = expense.approvals.find(s => s.stepNumber === expense.currentStep);
    return step?.approvers?.some(approver =>
      String(approver.userId) === currentUser.userId &&
      approver.status === 'pending'
    );
  };

  const hasUserApproved = (expense) => {
    if (!currentUser?.userId || !expense.approvals) return false;
    return expense.approvals.some(step =>
      step.approvers?.some(approver =>
        String(approver.userId) === currentUser.userId &&
        approver.status === 'approved'
      )
    );
  };

  const openCommentsDialog = (id, action) => {
    setSelectedExpenseId(id);
    setApprovalAction(action);
    setComments('');
    setCommentsDialogVisible(true);
  };

  const submitApprovalAction = async () => {
    if (!selectedExpenseId || !approvalAction) return;
    setLoading(true);
    setCommentsDialogVisible(false);
    try {
      const endpoint = `/api/app/expense_management/expenses/approve/${selectedFacilityId}/${selectedExpenseId}`;
      const approve = approvalAction === 'approve';
      const response = await makeRequest2(endpoint, 'PUT', {
        approve,
        comments,
        userId: currentUser.userId
      });
      if (response.success) {
        toastify(`Expense ${approve ? 'approved' : 'rejected'} successfully`, 'success');
        fetchExpenses();
        setShowExpenseDialog(false);
        setShowDayDialog(false);
      } else {
        toastify(response.error || 'Action failed', 'error');
      }
    } catch (error) {
      toastify('Error submitting action', 'error');
    } finally {
      setLoading(false);
      setSelectedExpenseId(null);
      setApprovalAction(null);
    }
  };

  const handleStatusUpdate = async (expenseId, newStatus) => {
    setLoading(true);
    try {
      const response = await makeRequest2(`/api/app/expense_management/expenses/update_status/${selectedFacilityId}/${expenseId}`, 'PUT', {
        status: newStatus
      });
      if (response.success) {
        toastify(`Expense status updated to ${newStatus}`, 'success');
        fetchExpenses();
        setShowExpenseDialog(false);
        setShowDayDialog(false);
      } else {
        toastify(response.error || 'Failed to update status', 'error');
      }
    } catch (error) {
      toastify('Error updating status', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Transform expenses into calendar events
  const events = expenses.map(expense => ({
    id: expense._id,
    title: expense.name,
    start: new Date(expense.date),
    end: new Date(expense.date),
    resource: expense,
    allDay: true
  }));

  // Custom event component with status indicators
  const EventComponent = ({ event }) => {
    const expense = event.resource;
    
    return (
      <div className="expense-event">
        <div className="d-flex align-items-center justify-content-between">
          <span className="expense-name" style={{ fontSize: '11px', fontWeight: 'bold' }}>
            {expense.name}
          </span>
          <div className="status-indicators">
            <Badge 
              value={expense.status} 
              severity={expense.status === 'PAID' ? 'success' : 'danger'}
              style={{ fontSize: '8px', marginRight: '2px' }}
            />
            <Badge 
              value={expense.approvalStatus} 
              severity={
                expense.approvalStatus === 'approved' ? 'success' :
                expense.approvalStatus === 'rejected' ? 'danger' :
                expense.approvalStatus === 'in_progress' ? 'warning' : 'info'
              }
              style={{ fontSize: '8px' }}
            />
          </div>
        </div>
        <div style={{ fontSize: '9px', color: '#fff', opacity: 0.9 }}>
          {formatCurrency(expense.amount)}
        </div>
      </div>
    );
  };

  // Handle event selection
  const handleSelectEvent = (event) => {
    setSelectedExpense(event.resource);
    setShowExpenseDialog(true);
  };

  // Handle date selection
  const handleSelectSlot = (slotInfo) => {
    const selectedDate = moment(slotInfo.start).format('YYYY-MM-DD');
    const expensesForDate = expenses.filter(expense => 
      moment(expense.date).format('YYYY-MM-DD') === selectedDate
    );
    
    setSelectedDate(slotInfo.start);
    setDayExpenses(expensesForDate);
    setShowDayDialog(true);
  };

  // Custom day cell component to show expense count
  const DayCellWrapper = ({ children, value }) => {
    const dayExpenses = expenses.filter(expense => 
      moment(expense.date).format('YYYY-MM-DD') === moment(value).format('YYYY-MM-DD')
    );

    const totalAmount = dayExpenses.reduce((sum, expense) => sum + (expense.amount || 0), 0);

    return (
      <div className="rbc-day-bg">
        {children}
        {dayExpenses.length > 0 && (
          <div className="expense-day-summary" style={{
            position: 'absolute',
            bottom: '2px',
            right: '2px',
            fontSize: '9px',
            backgroundColor: 'rgba(0,0,0,0.8)',
            color: 'white',
            padding: '1px 4px',
            borderRadius: '3px',
            fontWeight: 'bold'
          }}>
            {dayExpenses.length} | {formatCurrency(totalAmount)}
          </div>
        )}
      </div>
    );
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES'
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (date) => {
    return moment(date).format('MMMM Do, YYYY');
  };

  const getCategoryName = (expense) => {
    if (expense.CategoryInfo && expense.CategoryInfo.name) {
      return expense.CategoryInfo.name;
    }
    const category = categories.find(cat => cat._id === expense.categoryId);
    return category?.name || 'Unknown';
  };

  const getBudgetName = (expense) => {
    if (expense.BudgetInfo && expense.BudgetInfo.name) {
      return expense.BudgetInfo.name;
    }
    const budget = budgets.find(bud => bud._id === expense.budgetId);
    return budget?.name || budget?.budgetName || 'Unknown';
  };

  const commentsDialogFooter = (
    <div>
      <Button 
        label="Cancel" 
        icon="pi pi-times" 
        className="p-button-text" 
        onClick={() => setCommentsDialogVisible(false)} 
      />
      <Button
        label={approvalAction === 'approve' ? 'Approve' : 'Reject'}
        icon={approvalAction === 'approve' ? 'pi pi-check' : 'pi pi-times'}
        className={approvalAction === 'approve' ? 'p-button-success' : 'p-button-danger'}
        onClick={submitApprovalAction}
        loading={loading}
      />
    </div>
  );

  if (!selectedFacilityId) {
    return (
      <div className="row">
        <div className="col-lg-12">
          <div className="card">
            <div className="card-body text-center">
              Please select a facility to view expense calendar.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-lg-12">
        <div className="card">
          <div className="card-body">
            <style jsx>{`
              .expense-calendar {
                height: 700px;
                padding: 20px;
              }
              .rbc-calendar {
                height: 100%;
              }
              .expense-event {
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                border: none;
                border-radius: 6px;
                padding: 4px 6px;
                color: white;
                font-size: 11px;
                margin: 1px 0;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                box-shadow: 0 2px 4px rgba(0,0,0,0.1);
              }
              .expense-event:hover {
                background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
                transform: translateY(-1px);
                box-shadow: 0 4px 8px rgba(0,0,0,0.2);
              }
              .rbc-day-bg {
                position: relative;
              }
              .expense-day-summary {
                z-index: 1;
              }
              .rbc-event {
                background: none !important;
                border: none !important;
                padding: 0 !important;
              }
              .rbc-event-content {
                padding: 0 !important;
              }
              .calendar-legend {
                background: #f8f9fa;
                border-radius: 8px;
                padding: 10px;
                margin-bottom: 15px;
              }
              .legend-item {
                display: flex;
                align-items: center;
                font-size: 12px;
              }
              .calendar-controls .btn {
                margin-right: 5px;
              }
              .rbc-header {
                padding: 10px 5px;
                font-weight: 600;
                background: #f8f9fa;
                border-bottom: 1px solid #dee2e6;
              }
              .rbc-today {
                background-color: #e3f2fd;
              }
              .rbc-off-range-bg {
                background-color: #fafafa;
              }
              .status-indicators {
                display: flex;
                gap: 2px;
              }
            `}</style>

            <div className="calendar-header mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0">
                  <i className="ti ti-calendar me-2"></i>
                  Expense Calendar
                </h4>
                <div className="calendar-controls">
                  <button 
                    className="btn btn-outline-primary btn-sm me-2"
                    onClick={() => setDate(new Date())}
                  >
                    <i className="ti ti-current-location me-1"></i>
                    Today
                  </button>
                  <button 
                    className={`btn btn-sm me-1 ${view === 'month' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setView('month')}
                  >
                    Month
                  </button>
                  <button 
                    className={`btn btn-sm me-1 ${view === 'week' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setView('week')}
                  >
                    Week
                  </button>
                  <button 
                    className={`btn btn-sm ${view === 'day' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setView('day')}
                  >
                    Day
                  </button>
                </div>
              </div>
              
              {/* Legend */}
              <div className="calendar-legend">
                <div className="row">
                  <div className="col-12">
                    <div className="d-flex flex-wrap align-items-center gap-4">
                      <div className="legend-item">
                        <span className="badge bg-success me-2">PAID</span>
                        <span>Paid Expenses</span>
                      </div>
                      <div className="legend-item">
                        <span className="badge bg-warning me-2">UNPAID</span>
                        <span>Unpaid Expenses</span>
                      </div>
                      <div className="legend-item">
                        <span className="badge bg-success me-2">APPROVED</span>
                        <span>Approved</span>
                      </div>
                      <div className="legend-item">
                        <span className="badge bg-info me-2">PENDING</span>
                        <span>Pending</span>
                      </div>
                      <div className="legend-item">
                        <span className="badge bg-warning me-2">IN PROGRESS</span>
                        <span>In Progress</span>
                      </div>
                      <div className="legend-item">
                        <span className="badge bg-danger me-2">REJECTED</span>
                        <span>Rejected</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="expense-calendar">
              <Calendar
                localizer={localizer}
                events={events}
                startAccessor="start"
                endAccessor="end"
                style={{ height: '100%' }}
                view={view}
                onView={setView}
                date={date}
                onNavigate={setDate}
                onSelectEvent={handleSelectEvent}
                onSelectSlot={handleSelectSlot}
                selectable
                components={{
                  event: EventComponent,
                  dateCellWrapper: DayCellWrapper
                }}
                eventPropGetter={(event) => ({
                  className: 'expense-calendar-event',
                  style: {
                    backgroundColor: 'transparent',
                    border: 'none'
                  }
                })}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Expense Details Dialog */}
      <Dialog
        header="Expense Details"
        visible={showExpenseDialog}
        onHide={() => setShowExpenseDialog(false)}
        style={{ width: '600px' }}
        modal
      >
        {selectedExpense && (
          <div className="expense-details">
            <div className="row">
              <div className="col-12">
                <div className="card mb-3">
                  <div className="card-body">
                    <h5 className="card-title">{selectedExpense.name}</h5>
                    <div className="row">
                      <div className="col-md-6">
                        <p className="mb-2">
                          <strong>Amount:</strong> {formatCurrency(selectedExpense.amount)}
                        </p>
                        <p className="mb-2">
                          <strong>Date:</strong> {formatDate(selectedExpense.date)}
                        </p>
                        <p className="mb-2">
                          <strong>Type:</strong> {selectedExpense.type?.replace('_', ' ')}
                        </p>
                      </div>
                      <div className="col-md-6">
                        <p className="mb-2">
                          <strong>Category:</strong> {getCategoryName(selectedExpense)}
                        </p>
                        <p className="mb-2">
                          <strong>Budget:</strong> {getBudgetName(selectedExpense)}
                        </p>
                        <p className="mb-2">
                          <strong>Status:</strong> 
                          <span className={`badge ms-2 ${selectedExpense.status === 'PAID' ? 'bg-success' : 'bg-warning'}`}>
                            {selectedExpense.status}
                          </span>
                        </p>
                      </div>
                    </div>
                    <p className="mb-2">
                      <strong>Approval Status:</strong> 
                      <span className={`badge ms-2 ${
                        selectedExpense.approvalStatus === 'approved' ? 'bg-success' :
                        selectedExpense.approvalStatus === 'rejected' ? 'bg-danger' :
                        selectedExpense.approvalStatus === 'in_progress' ? 'bg-info' : 'bg-secondary'
                      }`}>
                        {selectedExpense.approvalStatus?.replace('_', ' ').toUpperCase()}
                      </span>
                    </p>
                    {selectedExpense.description && (
                      <p className="mb-0">
                        <strong>Description:</strong> {selectedExpense.description}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="expense-actions">
              <div className="d-flex justify-content-between">
                <div>
                  <button 
                    className="btn btn-success btn-sm me-2"
                    onClick={() => handleStatusUpdate(selectedExpense._id, 'PAID')}
                    disabled={selectedExpense.status === 'PAID' || loading}
                  >
                    <i className="ti ti-check me-1"></i>
                    Mark as Paid
                  </button>
                  <button 
                    className="btn btn-warning btn-sm"
                    onClick={() => handleStatusUpdate(selectedExpense._id, 'UNPAID')}
                    disabled={selectedExpense.status === 'UNPAID' || loading}
                  >
                    <i className="ti ti-clock me-1"></i>
                    Mark as Unpaid
                  </button>
                </div>
                <div>
                  {isUserApprover(selectedExpense) && 
                   ['pending', 'in_progress'].includes((selectedExpense.approvalStatus || '').toLowerCase()) && 
                   !hasUserApproved(selectedExpense) && (
                    <>
                      <button 
                        className="btn btn-success btn-sm me-2"
                        onClick={() => openCommentsDialog(selectedExpense._id, 'approve')}
                        disabled={loading}
                      >
                        <i className="ti ti-check me-1"></i>
                        Approve
                      </button>
                      <button 
                        className="btn btn-danger btn-sm"
                        onClick={() => openCommentsDialog(selectedExpense._id, 'reject')}
                        disabled={loading}
                      >
                        <i className="ti ti-x me-1"></i>
                        Reject
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </Dialog>

      {/* Day Expenses Dialog */}
      <Dialog
        header={`Expenses for ${selectedDate ? formatDate(selectedDate) : ''}`}
        visible={showDayDialog}
        onHide={() => setShowDayDialog(false)}
        style={{ width: '800px' }}
        modal
      >
        <div className="day-expenses">
          {dayExpenses.length === 0 ? (
            <div className="text-center py-4">
              <i className="ti ti-calendar-off" style={{ fontSize: '48px', color: '#ccc' }}></i>
              <p className="mt-2">No expenses for this date.</p>
            </div>
          ) : (
            <div>
              <div className="day-summary mb-4">
                <div className="row">
                  <div className="col-md-3">
                    <div className="card text-center bg-primary text-white">
                      <div className="card-body">
                        <h6 className="mb-1">Total Expenses</h6>
                        <h3 className="mb-0">{dayExpenses.length}</h3>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card text-center bg-info text-white">
                      <div className="card-body">
                        <h6 className="mb-1">Total Amount</h6>
                        <h3 className="mb-0">{formatCurrency(dayExpenses.reduce((sum, exp) => sum + (exp.amount || 0), 0))}</h3>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card text-center bg-success text-white">
                      <div className="card-body">
                        <h6 className="mb-1">Paid Amount</h6>
                        <h3 className="mb-0">{formatCurrency(dayExpenses.filter(exp => exp.status === 'PAID').reduce((sum, exp) => sum + (exp.amount || 0), 0))}</h3>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card text-center bg-warning text-white">
                      <div className="card-body">
                        <h6 className="mb-1">Unpaid Amount</h6>
                        <h3 className="mb-0">{formatCurrency(dayExpenses.filter(exp => exp.status === 'UNPAID').reduce((sum, exp) => sum + (exp.amount || 0), 0))}</h3>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="expenses-list">
                {dayExpenses.map(expense => (
                  <div key={expense._id} className="card mb-3">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          <h6 className="mb-1">{expense.name}</h6>
                          <p className="mb-1 text-primary fw-bold">{formatCurrency(expense.amount)}</p>
                          <small className="text-muted">{expense.type?.replace('_', ' ')}</small>
                          <div className="mt-1">
                            <small className="text-muted">Category: {getCategoryName(expense)}</small>
                          </div>
                        </div>
                        <div className="text-end">
                          <span className={`badge mb-1 ${expense.status === 'PAID' ? 'bg-success' : 'bg-warning'}`}>
                            {expense.status}
                          </span>
                          <br />
                          <span className={`badge ${
                            expense.approvalStatus === 'approved' ? 'bg-success' :
                            expense.approvalStatus === 'rejected' ? 'bg-danger' :
                            expense.approvalStatus === 'in_progress' ? 'bg-info' : 'bg-secondary'
                          }`}>
                            {expense.approvalStatus?.replace('_', ' ').toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="mt-3">
                        <button 
                          className="btn btn-primary btn-sm"
                          onClick={() => {
                            setSelectedExpense(expense);
                            setShowDayDialog(false);
                            setShowExpenseDialog(true);
                          }}
                        >
                          <i className="ti ti-eye me-1"></i>
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Dialog>

      {/* Comments Dialog */}
      <Dialog
        header={`${approvalAction === 'approve' ? 'Approve' : 'Reject'} Expense`}
        visible={commentsDialogVisible}
        style={{ width: '500px' }}
        footer={commentsDialogFooter}
        onHide={() => setCommentsDialogVisible(false)}
      >
        <div className="field">
          <label htmlFor="comments" className="form-label">Comments (Optional)</label>
          <InputTextarea
            id="comments"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={5}
            cols={30}
            className="w-100"
            placeholder="Enter any comments regarding your decision..."
          />
        </div>
      </Dialog>
    </div>
  );
};

export default ExpenseCalendarTab;